import { ProgressBar } from "./ProgressBar";
import { ProfileForm } from "./ProfileForm";
import { QuestionForm } from "./QuestionForm";
import { useTheme } from "@/components/ui/theme-provider";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { X } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface SurveyContentProps {
  currentQuestion: number;
  questions: Array<{
    id: string;
    question: string;
    options?: string[];
    question_type: string;
  }>;
  answers: Record<string, any>;
  profile: {
    fullName: string;
    company: string;
    email: string;
    telephone: string;
    contactConsent: boolean;
    privacyConsent: boolean;
    logoConsent: boolean;
  };
  onProfileChange: (field: string, value: string | boolean) => void;
  onAnswer: (value: string | string[]) => void;
  onBack: () => void;
  onNext: () => void;
  onProfileSubmit: () => void;
}

export const SurveyContent = ({
  currentQuestion,
  questions,
  answers,
  profile,
  onProfileChange,
  onAnswer,
  onBack,
  onNext,
  onProfileSubmit,
}: SurveyContentProps) => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const [showWelcome, setShowWelcome] = useState(() => {
    // Check localStorage on initial render
    const hasSeenWelcome = localStorage.getItem('hasSeenWelcome');
    return !hasSeenWelcome;
  });

  // Update localStorage when welcome state changes
  useEffect(() => {
    if (!showWelcome) {
      localStorage.setItem('hasSeenWelcome', 'true');
    }
  }, [showWelcome]);

  // Save survey progress to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('surveyProgress', JSON.stringify({
        currentQuestion,
        answers,
        profile,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  }, [currentQuestion, answers, profile]);

  // Scroll to the top of the container when current question changes
  useEffect(() => {
    if (containerRef.current) {
      // Smooth scroll to top of the container
      containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      
      // Also ensure the window is scrolled to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentQuestion]);

  if (showWelcome) {
    return (
      <Dialog open={showWelcome} onOpenChange={setShowWelcome}>
        <DialogContent className="sm:max-w-md border-[#00DC82]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center mb-4">
              Join the Waitlist
            </DialogTitle>
            <DialogDescription className="text-center space-y-4">
              <p className="text-lg">
                Thank you for your interest in Sentiri! This quick survey helps us understand your specific challenges so we can tailor our platform to your needs.
              </p>
              <p>
                By completing this survey, you'll secure your spot on our exclusive waitlist (limited to 50 companies).
              </p>
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center mt-6">
            <Button 
              onClick={() => setShowWelcome(false)}
              className="bg-[#00DC82] text-white hover:bg-[#00DC82]/90"
            >
              Start Survey
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col p-4 pt-16" ref={containerRef}>
      <div className="w-full max-w-lg mx-auto">
        <div className="relative bg-card rounded-lg shadow-lg border border-[#00DC82] bg-background">
          <div className="absolute inset-0 opacity-[0.03] bg-gradient-to-br from-[#00DC82] to-[#00A3FF]" />
          <div className="relative p-6">
            <button
              onClick={() => navigate("/")}
              className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </button>
            <ProgressBar
              currentQuestion={currentQuestion}
              totalQuestions={questions.length}
            />

            <h3 className="text-2xl font-bold mb-6">
              {questions[currentQuestion].question}
            </h3>

            {questions[currentQuestion].question_type === 'profile' ? (
              <ProfileForm
                profile={profile}
                onProfileChange={onProfileChange}
                onNext={onProfileSubmit}
              />
            ) : (
              <QuestionForm
                question={questions[currentQuestion]}
                answer={answers[questions[currentQuestion].id] || (questions[currentQuestion].question_type === 'checkbox' ? [] : '')}
                onAnswerChange={onAnswer}
                onBack={onBack}
                onNext={onNext}
                isLastQuestion={currentQuestion === questions.length - 1}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};